/**
 * MANUALLY MAINTAINED — edit freely.
 *
 * Numeric values come from ItemStatus.js, which is also hand-maintained (its
 * "auto-generated" header was false and has been corrected — there is no
 * generator, and never was).
 *
 * This file is the SINGLE authoritative source for item-status display metadata,
 * transitions and business categories. ItemStatus.js holds the numeric enum and
 * nothing else. Components and composables import categories from HERE.
 *
 * i18nKey is a prefix: append '.label' for the status chip,
 * '.description' for the timeline entry.
 *   t(`${meta.i18nKey}.label`)        → "Collecting"
 *   t(`${meta.i18nKey}.description`)  → "Our collector is working on it"
 */

import { ItemStatus } from './ItemStatus.js';

// ─── Status display metadata ───────────────────────────────────────────────────
// Colors use Framework7 named colors: gray, blue, yellow, green, purple, teal,
// orange, red — no custom color names allowed here.

export const ItemStatusMeta = {
  [ItemStatus.PENDING]: {
    i18nKey: 'item.status.pending',
    color:   'orange',
    icon:    'clock',
  },
  [ItemStatus.COLLECTING]: {
    i18nKey: 'item.status.collecting',
    color:   'blue',
    icon:    'arrow_clockwise',
  },
  [ItemStatus.AWAITING_REVIEW]: {
    i18nKey: 'item.status.awaitingReview',
    color:   'yellow',
    icon:    'eye',
  },
  [ItemStatus.COLLECTED]: {
    i18nKey: 'item.status.collected',
    color:   'green',
    icon:    'checkmark_circle',
  },
  [ItemStatus.PACKAGED]: {
    i18nKey: 'item.status.packaged',
    color:   'purple',
    icon:    'cube_box',
  },
  [ItemStatus.SHIPPED]: {
    i18nKey: 'item.status.shipped',
    color:   'teal',
    icon:    'airplane',
  },
  [ItemStatus.DELIVERED]: {
    i18nKey: 'item.status.delivered',
    color:   'green',
    icon:    'checkmark_seal',
  },
  [ItemStatus.ISSUE]: {
    i18nKey: 'item.status.issue',
    color:   'red',
    icon:    'exclamationmark_triangle',
  },
  [ItemStatus.AWAITING_APPROVAL]: {
    i18nKey: 'item.status.awaitingApproval',
    color:   'orange',
    icon:    'hand_raised',
  },
  [ItemStatus.OUT_OF_STOCK]: {
    i18nKey: 'item.status.outOfStock',
    color:   'red',
    icon:    'xmark_octagon',
  },
  [ItemStatus.CANCELLED]: {
    i18nKey: 'item.status.cancelled',
    color:   'gray',
    icon:    'xmark_circle',
  },
  // CANCELLED's label is OVERRIDABLE — see `itemStatusLabelKey()` right below
  // this object, which swaps it for one line: the honest "not what you ordered"
  // close. There is deliberately no ItemStatus for that ending and there must
  // not be — the backend closes the line as CANCELLED and reports WHY in a
  // separate `lossReason`, so minting a numeric status here would put a value in
  // the enum the server never sends.

  // Lost / damaged / returned unfit AFTER collection. Without this entry the
  // member saw an unlabelled gray chip on a terminal status — meta lookup misses,
  // i18nKey is undefined, label resolves to ''. Red mirrors the backend's own
  // colour() for this case and groups it with the other unfulfilled outcomes.
  [ItemStatus.WRITTEN_OFF]: {
    i18nKey: 'item.status.writtenOff',
    color:   'red',
    icon:    'trash',
  },
};

/**
 * Why a terminal line ended the way it did (`App\Enums\Commerce\ItemLossReason`).
 *
 * Only ONE value changes what the member reads, and it is the whole point of
 * the enum reaching the PWA at all. The rest are post-collection write-offs
 * already covered by WRITTEN_OFF's own chip.
 */
export const ItemLossReason = {
  // The shop HAD the product — it just was not her version. She wanted the
  // Mickey print; they had cats and dogs.
  NOT_AS_ORDERED: 'not_as_ordered',
};

/**
 * The i18n key PREFIX for a line's status chip — the one place the loss reason
 * is allowed to change what she reads.
 *
 * A `CANCELLED` line carrying `not_as_ordered` must read as neither of the two
 * sentences the codebase already has for it:
 *
 *   «ملغي» is wrong because it reads as though SHE cancelled it. She did not;
 *          the club closed the line after seeing the shelf.
 *   «نواقص» is wrong because it is a false claim about the product. It was
 *          sitting right there — this is the honest fourth ending that exists
 *          precisely so the club stops saying "out of stock" about a product it
 *          was looking at (34 questions on record ended that way).
 *
 * And nothing here may promise money back. Nothing was bought and nothing was
 * charged, in either direction, so there is no refund to describe — the Arabic
 * copy is banned from استرداد / رجّعنا / تعويض for the same reason.
 *
 * THE COLOUR DOES NOT MOVE. It stays CANCELLED's grey, read from the meta by
 * status alone: this is a quiet, blameless ending, not a warning, and promoting
 * it to red would put it beside the failures it is not one of.
 *
 * Every other status, and every other loss reason, is unchanged — an unknown
 * value falls through to the plain status label rather than rendering a raw key.
 *
 * @param {number} status      an ItemStatus value (already coerced to a number)
 * @param {string|null} [lossReason]  the item's `lossReason`, omitted when absent
 * @returns {string|undefined} the i18n prefix — append '.label' / '.description'
 */
export function itemStatusLabelKey(status, lossReason = null) {
  if (status === ItemStatus.CANCELLED && lossReason === ItemLossReason.NOT_AS_ORDERED) {
    return 'item.status.notAsOrdered';
  }
  return ItemStatusMeta[status]?.i18nKey;
}

// ─── State machine ─────────────────────────────────────────────────────────────

export const ItemStatusTransitions = {
  [ItemStatus.PENDING]: [
    ItemStatus.COLLECTING,
    ItemStatus.CANCELLED,
  ],
  [ItemStatus.COLLECTING]: [
    ItemStatus.AWAITING_REVIEW,
    ItemStatus.ISSUE,
    ItemStatus.OUT_OF_STOCK,
  ],
  [ItemStatus.AWAITING_REVIEW]: [
    ItemStatus.COLLECTED,
    ItemStatus.OUT_OF_STOCK,
    ItemStatus.PENDING,
    ItemStatus.ISSUE,
  ],
  // AWAITING_APPROVAL (32) is RETIRED as a written state (2026-09-02, status
  // pack). Nothing walks INTO it any more — see the removed edges above and
  // below. This row is a one-way exit that exists only so a straggler row still
  // resting at 32 can be walked forward to ISSUE by the backend's nightly
  // reconcile. Mirrors ItemStatus::canTransitionTo() in the backend enum.
  [ItemStatus.AWAITING_APPROVAL]: [
    ItemStatus.ISSUE,
  ],
  [ItemStatus.ISSUE]: [
    ItemStatus.COLLECTING,
    ItemStatus.OUT_OF_STOCK,
    ItemStatus.CANCELLED,
  ],
  [ItemStatus.COLLECTED]: [
    ItemStatus.PACKAGED,
    ItemStatus.CANCELLED,
  ],
  [ItemStatus.PACKAGED]: [
    ItemStatus.SHIPPED,
    ItemStatus.COLLECTED,
  ],
  [ItemStatus.SHIPPED]: [
    ItemStatus.DELIVERED,
  ],
  [ItemStatus.CANCELLED]: [
    ItemStatus.PENDING, // can be restored
  ],
  [ItemStatus.DELIVERED]:    [],
  // 41 → 7 is the ONE new edge of the hold work (oos-hold P1-A, brief
  // 2026-09-15-oos-line-cancel-and-the-hold, owner answer H-M). The member may
  // cancel a line nobody could find while its carton can still take the item
  // back, which releases the money her line was still holding.
  //
  // 41 → 1 (a staff revive) is real on the backend and deliberately NOT here:
  // this table is member-facing, and no member surface drives that edge.
  [ItemStatus.OUT_OF_STOCK]: [
    ItemStatus.CANCELLED,
  ],
  // Terminal for the member. The backend does allow an admin to reinstate a
  // written-off item to the status it held at authorization, but no member-facing
  // surface drives that, so this table — which is member-facing — leaves it empty.
  [ItemStatus.WRITTEN_OFF]:  [],
};

// ─── Status categories ─────────────────────────────────────────────────────────
// These are the single source of truth for all business-rule checks.
// Components and composables import from here — never hardcode status arrays.

export const ItemStatusCategories = {
  ACTIVE: [
    ItemStatus.PENDING,
    ItemStatus.COLLECTING,
    ItemStatus.AWAITING_REVIEW,
    ItemStatus.COLLECTED,
    ItemStatus.PACKAGED,
  ],
  IN_TRANSIT: [
    ItemStatus.SHIPPED,
  ],
  // AWAITING_APPROVAL (32) dropped 2026-09-02 (status pack, P3-A) — it is
  // retired as a written state, so no item can rest there any more. See
  // ../../../../backend/app/Enums/Commerce/ItemStatus.php.
  NEEDS_ATTENTION: [
    ItemStatus.ISSUE,
    ItemStatus.OUT_OF_STOCK,
  ],
  COMPLETED: [
    ItemStatus.DELIVERED,
  ],
  // Mirrors the backend's nonActiveCases() — the lines that carry no money and
  // no fulfilment obligation.
  FAILED: [
    ItemStatus.OUT_OF_STOCK,
    ItemStatus.CANCELLED,
    ItemStatus.WRITTEN_OFF,
  ],
  // Mirrors the backend's ItemStatus::isFinal().
  TERMINAL: [
    ItemStatus.DELIVERED,
    ItemStatus.OUT_OF_STOCK,
    ItemStatus.WRITTEN_OFF,
  ],
  // The goods are physically in hand — the client-side read of "this item's
  // CollectionPlanItem outcome is COLLECTED", which the item-issue resource does
  // not expose directly.
  //
  // Sound because the two are written in ONE transaction:
  // `CollectionPlanService::leaderMarkCollected()` walks the OrderBoxItem to
  // COLLECTED and sets `outcome = COLLECTED` together, and every status after it
  // (PACKAGED / SHIPPED / DELIVERED, and WRITTEN_OFF — lost or damaged AFTER
  // collection) leaves that outcome standing. The inverse holds too: a question
  // raised on a NOT-collected item walks the item to ISSUE → AWAITING_APPROVAL
  // (`ItemIssueService::ask()`), and that branch is skipped entirely when the
  // item is already collected — so the two sets never overlap.
  //
  // Load-bearing for money: `ItemIssueService::collect()` returns at its
  // COLLECTED branch BEFORE any offered option is applied, so on these statuses
  // no answer the member gives moves the line total.
  GOODS_IN_HAND: [
    ItemStatus.COLLECTED,
    ItemStatus.PACKAGED,
    ItemStatus.SHIPPED,
    ItemStatus.DELIVERED,
    ItemStatus.WRITTEN_OFF,
  ],
  // Items where the Edit button is shown (covers qty stepper + note input).
  //
  // OWNER-LOCKED 2026-08-09 — COLLECTING is deliberately NOT here. A member must
  // not edit quantity, edit notes, or cancel while an item is COLLECTING: a
  // collector is physically shopping for it, and a change underneath them races
  // the real world. This used to include COLLECTING and was saved only by the
  // model reading a stale duplicate of this object first — the correct behaviour
  // was winning by accident. It is now intentional, and there is one object.
  EDITABLE: [
    ItemStatus.PENDING,
    ItemStatus.CANCELLED, // shows restore, not qty edit — useItemStatus handles the distinction
  ],
  // The narrow gate for the member's own line fields — qty stepper, note,
  // avatar, reservation. Every one of those four reads THIS list in
  // useItemStatus, rather than deriving from !isReadOnly, so widening isReadOnly
  // can never silently re-open editing mid-collection. Named for the quantity
  // stepper historically; it gates all four fields.
  QUANTITY_EDITABLE: [
    ItemStatus.PENDING,
  ],
  // Statuses a MEMBER may cancel. Mirrors OrderBoxService::memberCancelItem's
  // source-status gate — anything other than PENDING throws.
  //
  // The backend refuses a member cancel in TWO different places, and they fail
  // very differently, so keep them straight:
  //
  //  1. WRONG STATUS, not in an active plan (an ISSUE or COLLECTED line whose
  //     plan has finished). OrderBoxItemPolicy::cancel passes, the service
  //     throws InvalidArgumentException → 422 → ORDER_BOX_ITEM_STALE_STATE.
  //     VariantItemsEditor renders `error.detail` in preference to its own
  //     copy, and that detail is the exception's ENGLISH sentence — so this one
  //     really does answer an Arabic member in English. THIS list closes it.
  //
  //  2. IN AN ACTIVE PLAN. OrderBoxItemPolicy::cancel returns false BEFORE the
  //     service runs → 403 → ORDER_BOX_ITEM_PLAN_LOCKED, which the PWA already
  //     handles properly: Arabic `item.status.errors.planLocked`, plus the id
  //     goes into `planLockedItemIds` so the control disables from then on.
  //
  //     CLOSED 2026-09-07 (ASK 1, answered as `inActivePlan` — brief
  //     sync/briefs/2026-09-07-member-contract-round-two.md). The resource now
  //     emits `inActivePlan: true` when a collector is out buying the line,
  //     reading the same predicate the write lock uses, so the PWA withholds
  //     Cancel up front rather than offering it until the first refusal. THAT
  //     GATE IS NOT IN THIS LIST — it lives in `useItemStatus.canCancel`,
  //     because this file maps STATUSES and `inActivePlan` is a per-item fact.
  //     Keep the two together in your head: status says "could this ever be
  //     cancelled", the fact says "right now".
  //
  //     The 403 handling stays: a line whose fact has not been re-read since the
  //     collector picked it up still refuses, and that path is still localized.
  //
  //  3. OUT_OF_STOCK, ADDED 2026-09-15 (oos-hold P1-A, owner answer H-M). Her
  //     money for a line nobody could find is STILL HELD — it counts toward her
  //     committed balance for as long as the carton can take the item back —
  //     and cancelling is how she releases it. `OrderBoxService::memberCancelItem()`
  //     admits it inside that window and refuses outside it with a 422 whose
  //     detail is now her own Arabic sentence.
  //
  //     ⚠ THE WINDOW IS NOT A STATUS, so it is not in this list either. It is
  //     the CARTON's fact — ACTIVE or READY and not delivery-confirmed — and it
  //     lives in `useItemStatus.canCancel`, reading the one shared
  //     `boxCanTakeItBack()` the suggest door already reads. Keep the three
  //     apart in your head: this list says "could this status ever be
  //     cancelled", `inActivePlan` says "is anyone out buying it right now",
  //     and the carton says "is there anywhere left to put it back".
  MEMBER_CANCELLABLE: [
    ItemStatus.PENDING,
    ItemStatus.OUT_OF_STOCK,
  ],
  // Statuses on which a member may ASK to cancel a line a collector is already
  // out buying (BE→FE brief 2026-09-21-member-cancel-request): nothing has been
  // bought yet. Mirrors `OrderBoxItem::CANCEL_REQUESTABLE_STATUSES` in
  // ../backend/app/Models/Commerce/OrderBoxItem.php — read it there, not here.
  //
  // The other half of the gate is `inActivePlan`, a per-item fact, so like
  // MEMBER_CANCELLABLE's plan gate it is not in this list: it lives in
  // `canRequestCancel()` (models/commerce/OrderBoxItem.js). The two doors are
  // exact complements on a PENDING line — «الغي» off a live plan, «اطلب الإلغاء»
  // on one.
  MEMBER_CANCEL_REQUESTABLE: [
    ItemStatus.PENDING,
    ItemStatus.COLLECTING,
    ItemStatus.ISSUE,
  ],
  // Items that can be restored to PENDING
  RESTORABLE: [
    ItemStatus.CANCELLED,
  ],
  // Items that block a shipping request — the member MUST resolve these first.
  // OUT_OF_STOCK is deliberately NOT here: per the backend contract it is
  // terminal / awareness-only (ItemStatus::isFinal, and it sits in
  // nonActiveCases so it is already excluded from box totals). A member cannot
  // "solve" an OOS row, so it must never gate shipping nor fire the
  // "solve issues first" banner. Drives the ship-request gate + the orderbox
  // banner count — those two must always agree, so both read this ONE list.
  BLOCKS_SHIPPING: [
    ItemStatus.ISSUE,
  ],
  // Lines still ON THEIR WAY to the carton — the ask-to-ship gate refuses while
  // any of these is present (ship-ready pack, S-A; backend
  // `ItemStatus::onItsWayCases()`, which the server enforces with a 409
  // `ship_request.lines_on_the_way`). Booked on a collection plan or not makes no
  // difference; the status alone decides.
  //
  // A SIBLING of BLOCKS_SHIPPING, deliberately not a widening of it. That list
  // is "an issue she must RESOLVE" and also drives the carton card's «N منتج
  // بمشاكل» banner, so putting pending lines in it would tell her that an
  // ordinary in-progress line is a problem. This one answers a different
  // question — "is everything I ordered either here or given up on?" — and the
  // remedy is different too: wait, or cancel the ones she no longer wants.
  // ISSUE is in both on purpose: the button reads BLOCKS_SHIPPING first («حل
  // المشاكل أولًا») and only then falls through to this list.
  //
  // 32 (AWAITING_APPROVAL) is retired as a written state but its value is
  // reserved and the backend still counts it, so it stays here. OUT_OF_STOCK is
  // NOT on the way — it is handled by her explicit approval, see the confirm.
  ON_ITS_WAY: [
    ItemStatus.PENDING,
    ItemStatus.COLLECTING,
    ItemStatus.AWAITING_REVIEW,
    ItemStatus.ISSUE,
    ItemStatus.AWAITING_APPROVAL,
  ],
  // Lines that have ARRIVED — the collectors hold them, so there is something to
  // pack. A carton with none of these is refused (409
  // `ship_request.nothing_arrived`): a ship request must never put an empty
  // carton on the packing desk.
  ARRIVED: [
    ItemStatus.COLLECTED,
    ItemStatus.PACKAGED,
  ],
  // Items the member can still act on. The old change-supplier / wait-for-restock
  // / cancel resolution sheet this was written for NO LONGER EXISTS — it was
  // retired by the member-answerable collection-issue work. What this drives now
  // is the member-facing issue surface: the "decisions needed" inbox entry, the
  // issue detail + timeline route, and the issue step in the item timeline.
  // OUT_OF_STOCK is included by product decision so a member can still open it
  // and read what happened — intentionally DECOUPLED from BLOCKS_SHIPPING
  // (being able to look at an item ≠ that item blocking the shipment).
  RESOLUTION_ACTIONABLE: [
    ItemStatus.ISSUE,
    ItemStatus.OUT_OF_STOCK,
  ],
};

// ─── Convenience exports ────────────────────────────────────────────────────────
// Import these instead of reaching into ItemStatusCategories directly.

export const EDITABLE_STATUSES            = ItemStatusCategories.EDITABLE;
export const BLOCKS_SHIPPING_STATUSES     = ItemStatusCategories.BLOCKS_SHIPPING;
export const RESOLUTION_ACTIONABLE_STATUSES = ItemStatusCategories.RESOLUTION_ACTIONABLE;
export const { QUANTITY_EDITABLE } = ItemStatusCategories;
